import { DiscordSDK } from '@discord/embedded-app-sdk';

// Discord SDK instance (only initialized when running in Discord)
let discordSdk: DiscordSDK | null = null;
let isDiscordEnvironment = false;
let discordUser: any = null;
let discordGuild: any = null;
let discordChannel: any = null;

/**
 * Detects if the app is running inside Discord
 */
export function isRunningInDiscord(): boolean {
  // Check for Discord-specific URL parameters or iframe context
  const urlParams = new URLSearchParams(window.location.search);
  const hasDiscordParams = urlParams.has('frame_id') || urlParams.has('instance_id') || urlParams.has('client_id');
  
  // Check if we're in an iframe (Discord Activities run in iframes)
  const isInIframe = window !== window.parent;
  
  // Check for Discord-specific user agent or other indicators
  const hasDiscordUserAgent = navigator.userAgent.includes('Discord');
  
  // Check for Discord-specific domains
  const isDiscordDomain = window.location.hostname.includes('discordsays.com') ||
                         window.location.hostname.includes('discord.com');
  
  return hasDiscordParams || isDiscordDomain || (isInIframe && hasDiscordUserAgent);
}

/**
 * Initialize Discord SDK (only if running in Discord)
 */
export async function initializeDiscord(): Promise<boolean> {
  if (!isRunningInDiscord()) {
    console.log('Not running in Discord, skipping Discord initialization');
    return false;
  }

  try {
    // Get Discord client ID from environment or URL params
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = process.env.REACT_APP_DISCORD_CLIENT_ID || urlParams.get('client_id');
    
    console.log('🔍 Discord initialization debug:', {
      hasClientIdFromEnv: !!process.env.REACT_APP_DISCORD_CLIENT_ID,
      hasClientIdFromUrl: !!urlParams.get('client_id'),
      clientId: clientId ? `${clientId.substring(0, 8)}...` : 'none',
      urlParams: Object.fromEntries(urlParams.entries()),
      userAgent: navigator.userAgent,
      hostname: window.location.hostname,
      isInIframe: window !== window.parent
    });
    
    if (!clientId) {
      console.warn('Discord client ID not found, Discord features disabled');
      return false;
    }

    console.log('🎮 Creating Discord SDK instance...');
    discordSdk = new DiscordSDK(clientId);
    
    console.log('⏳ Waiting for Discord to be ready...');
    
    // Add timeout to Discord ready call
    const readyPromise = discordSdk.ready();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Discord SDK ready timeout')), 10000)
    );
    
    await Promise.race([readyPromise, timeoutPromise]);
    console.log('✅ Discord SDK ready!');
    
    console.log('🔐 Starting Discord authorization...');
    
    // For Discord Activities, we need to authenticate differently
    // First, try to authenticate without explicit authorization (for embedded activities)
    try {
      console.log('👤 Attempting direct authentication...');
      const authResult = await discordSdk.commands.authenticate({
        access_token: null, // Let Discord handle this
      });
      
      discordUser = authResult.user;
      console.log('✅ User authenticated directly:', {
        id: discordUser?.id,
        username: discordUser?.username,
        globalName: discordUser?.global_name
      });
      
      isDiscordEnvironment = true;
    } catch (directAuthError) {
      console.log('Direct auth failed, trying authorization flow...');
      
      // If direct auth fails, try the authorization flow with minimal scopes
      try {
        const authResponse = await discordSdk.commands.authorize({
          client_id: clientId,
          response_type: 'code',
          state: '',
          prompt: 'none',
          scope: ['identify'], // Minimal scope for Activities
        });

        console.log('Discord authorization response:', {
          hasCode: !!authResponse.code,
          code: authResponse.code ? `${authResponse.code.substring(0, 8)}...` : 'none'
        });
        
        // Now authenticate with the authorization code
        const authResult = await discordSdk.commands.authenticate({
          access_token: null,
        });
        
        discordUser = authResult.user;
        console.log('✅ User authenticated via authorization:', {
          id: discordUser?.id,
          username: discordUser?.username,
          globalName: discordUser?.global_name
        });
        
        isDiscordEnvironment = true;
      } catch (authFlowError) {
        console.error('Both authentication methods failed:', {
          directAuthError: directAuthError,
          authFlowError: authFlowError
        });
        
        // For Activities, we might still be able to get basic info without full auth
        try {
          console.log('Attempting to get basic activity context...');
          const participants = await discordSdk.commands.getInstanceConnectedParticipants();
          if (participants && participants.participants && participants.participants.length > 0) {
            // Use the first participant as the current user (common in single-user activities)
            discordUser = participants.participants[0];
            console.log('✅ Got user from activity context:', {
              id: discordUser?.id,
              username: discordUser?.username,
              globalName: discordUser?.global_name
            });
            isDiscordEnvironment = true;
          } else {
            throw new Error('No participants found in activity context');
          }
        } catch (contextError) {
          console.error('Failed to get activity context:', contextError);
          throw authFlowError;
        }
      }
    }
    
    // Get guild and channel info from the context
    try {
      console.log('🏰 Getting guild/channel info...');
      const participants = await discordSdk.commands.getInstanceConnectedParticipants();
      console.log('Connected participants:', participants);
      // Extract guild/channel info from context if available
      discordGuild = { id: 'unknown' }; // Placeholder - actual implementation depends on your needs
      discordChannel = { id: 'unknown' }; // Placeholder - actual implementation depends on your needs
    } catch (error) {
      console.warn('Could not get guild/channel info:', {
        error: error,
        message: error instanceof Error ? error.message : String(error)
      });
    }
    
    console.log('✅ Discord SDK initialized successfully', {
      user: discordUser,
      guild: discordGuild,
      channel: discordChannel,
    });
    
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Discord SDK:', {
      error: error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
    return false;
  }
}

/**
 * Get Discord user information (returns null if not in Discord)
 */
export function getDiscordUser() {
  return isDiscordEnvironment ? discordUser : null;
}

/**
 * Get Discord guild information (returns null if not in Discord)
 */
export function getDiscordGuild() {
  return isDiscordEnvironment ? discordGuild : null;
}

/**
 * Get Discord channel information (returns null if not in Discord)
 */
export function getDiscordChannel() {
  return isDiscordEnvironment ? discordChannel : null;
}

/**
 * Generate a session ID that works for both Discord and web
 */
export function generateSessionId(): string {
  if (isDiscordEnvironment && discordUser) {
    // Use Discord user ID + guild ID for consistent sessions
    const guildId = discordGuild?.id || 'dm';
    return `discord_${discordUser.id}_${guildId}`;
  }
  
  // For web users, use localStorage or generate a random ID
  let webSessionId = localStorage.getItem('ai-game-session-id');
  if (!webSessionId) {
    webSessionId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ai-game-session-id', webSessionId);
  }
  
  return webSessionId;
}

/**
 * Get user display name (works for both Discord and web)
 */
export function getUserDisplayName(): string {
  if (isDiscordEnvironment && discordUser) {
    return discordUser.global_name || discordUser.username || 'Discord User';
  }
  
  // For web users, use localStorage or default
  return localStorage.getItem('ai-game-username') || 'Player';
}

/**
 * Set user display name for web users
 */
export function setUserDisplayName(name: string): void {
  if (!isDiscordEnvironment) {
    localStorage.setItem('ai-game-username', name);
  }
}

/**
 * Check if Discord features are available
 */
export function hasDiscordFeatures(): boolean {
  return isDiscordEnvironment && discordSdk !== null;
}

/**
 * Get Discord SDK instance (for internal use)
 */
export function getDiscordSDK(): DiscordSDK | null {
  return discordSdk;
}

/**
 * Send activity update to Discord (if available)
 */
export async function updateDiscordActivity(activity: {
  details?: string;
  state?: string;
  largeImageKey?: string;
  largeImageText?: string;
  smallImageKey?: string;
  smallImageText?: string;
}): Promise<void> {
  if (!hasDiscordFeatures() || !discordSdk) {
    console.log('Discord features not available, skipping activity update');
    return;
  }

  // Check if we have a valid user (indicating successful authentication)
  if (!discordUser) {
    console.warn('Discord user not authenticated, skipping activity update');
    return;
  }

  try {
    console.log('🎮 Updating Discord activity:', activity);
    await discordSdk.commands.setActivity({
      activity: {
        type: 0, // Playing
        ...activity,
      },
    });
    console.log('✅ Discord activity updated successfully');
  } catch (error) {
    // Handle specific Discord error codes
    if (error && typeof error === 'object' && 'code' in error) {
      const discordError = error as { code: number; message: string };
      
      if (discordError.code === 4006) {
        console.error('❌ Discord authentication failed - Activities may not be properly configured');
        console.error('This usually means:');
        console.error('1. The Discord application is not configured for Activities');
        console.error('2. The app is not running in a proper Discord Activity context');
        console.error('3. Authentication scopes are insufficient');
        return; // Don't throw for auth errors, just log and continue
      }
    }
    
    // Properly serialize the error for Discord's logging system
    const errorDetails = {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      code: error && typeof error === 'object' && 'code' in error ? error.code : undefined,
      activity: JSON.stringify(activity),
      hasDiscordSdk: !!discordSdk,
      isDiscordEnvironment: isDiscordEnvironment,
      hasDiscordUser: !!discordUser
    };
    
    console.error('Failed to update Discord activity:', errorDetails);
    console.error('Raw error object:', error);
    throw error; // Re-throw to see the error in the calling code
  }
}

/**
 * Close Discord activity (if running in Discord)
 */
export function closeDiscordActivity(): void {
  if (hasDiscordFeatures() && discordSdk) {
    discordSdk.close(1000, 'Game completed');
  }
}